import { ClockCircleFilled } from '@ant-design/icons';
import { CalendarIcon, GlobeIcon } from '@heroicons/react/outline';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import moment from 'moment';

import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import StarRating from '../../BookLandingPageComp/StarRating';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import Crown from '../../../../assets/Icons/Crown';
import DiplomaIcon from '../../../../assets/Icons/DiplomaIcon';
import SubTitle from '../../../../assets/Icons/SubTitle';

export type CourseInfoProps = {
  data: {
    uploadedDate: string;
    language: string;
    rating: number;
    totalRatings: number;
    duration: string;
    courseLevel: string;
  };
};

const CourseInfo: React.FC<CourseInfoProps> = (data: CourseInfoProps) => {
  const {
    uploadedDate,
    language,
    rating,
    totalRatings,
    duration,
    courseLevel,
  } = data.data;
  const screens = useBreakpoint();
  const isSmallScreen =
    typeof window !== 'undefined' && window.innerWidth < 375;
  const inlineComp = (
    <div className='flex flex-col gap-y-4'>
      {/* Main Course Details */}
      <div className='flex flex-wrap gap-4 sm:flex-nowrap'>
        <span className='flex gap-x-2 items-center text-sm font-medium'>
          <CalendarIcon className='text-[#9F9EA6] w-5 h-5' />
          Uploaded on {moment(uploadedDate).format('MMM DD, YYYY')}
        </span>
        <span className='flex gap-x-2 items-center text-sm font-medium'>
          <GlobeIcon className='text-[#9F9EA6] w-5 h-5' />
          {language}
        </span>
        <span className='flex gap-x-2 items-center text-sm'>
          <SubTitle />
          Subtitle Available
        </span>
      </div>

      <div className='w-full bg-white shadow-lg rounded-[12px] border border-gray-200'>
        <div className='flex flex-wrap sm:flex-nowrap items-stretch h-full justify-between'>
          {/* Lifetime Access */}
          <div className='flex-1 bg-primary flex flex-col items-center justify-center py-3 px-4 text-white font-semibold text-sm md:text-base rounded-tl-[12px] sm:rounded-bl-[12px]'>
            <Crown />
            <span className='text-center text-sm max-w-[70px]'>
              Lifetime Access
            </span>
          </div>

          {/* Course Level */}
          <div className='text-center flex flex-col items-center justify-center px-4 py-3 border-r-[2px] gap-y-1.5'>
            <p className='text-black capitalize font-bold text-sm md:text-base 2xl:text-lg'>
              {courseLevel}
            </p>
            <div className='flex justify-center'>
              <p className='font-medium max-w-[90px] text-[#504F53] text-xs 2xl:text-sm'>
                Level Course
              </p>
            </div>
          </div>

          {/* Divider */}

          {/* Rating Section */}
          <ShouldRender check={rating}>
            <div className='text-center flex-1 flex flex-col items-center justify-center border-r-[2px] py-2.5 px-5 gap-y-1'>
              <p className='text-black text-2xl font-bold'>{rating}</p>
              <div className='flex justify-center items-center whitespace-nowrap'>
                <StarRating rating={rating} color='#CA8A04' />
              </div>
              <p
                onClick={() => handleScrollToBottom()}
                className='text-gray-500 cursor-pointer text-sm underline font-medium'
              >
                {totalRatings} ratings
              </p>
            </div>
          </ShouldRender>

          {/* Course Duration */}
          <div className='text-center flex-1 px-5 py-3 flex flex-col gap-y-1 justify-center items-center'>
            <ClockCircleFilled className='text-black text-2xl mb-1' />
            <p className='text-black text-base font-bold'>{duration}</p>
            <p className='text-[#504F53] text-sm font-medium'>Length</p>
          </div>

          {/* Certificate */}
          <div className='flex-1 bg-[#282829] flex flex-col items-center justify-center py-3 px-4 text-white font-semibold text-sm md:text-base rounded-tr-[12px] sm:rounded-br-[12px]'>
            <DiplomaIcon />
            <span className='text-center text-sm max-w-[70px]'>
              Earn a Certificate
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const handleScrollToBottom = () => {
    if (typeof window !== 'undefined') {
      const target = document.getElementById('review-container');
      if (target) {
        const targetPosition =
          target.getBoundingClientRect().top + window.scrollY - 100;
        programmaticScrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  const lgComp = (
    <div className='flex flex-col gap-y-4'>
      {/* Main Course Details */}
      <div className='flex flex-wrap gap-4 sm:flex-nowrap'>
        <span className='flex gap-x-2 items-center text-sm font-medium'>
          <CalendarIcon className='text-[#9F9EA6] w-5 h-5' />
          Uploaded on {moment(uploadedDate).format('MMM DD, YYYY')}
        </span>
        <span className='flex gap-x-2 items-center text-sm font-medium'>
          <GlobeIcon className='text-[#9F9EA6] w-5 h-5' />
          {language}
        </span>
        <span className='flex gap-x-2 items-center text-sm'>
          <SubTitle />
          Subtitle Available
        </span>
      </div>

      <div className='w-full bg-white shadow-lg flex-col flex rounded-[12px] border border-gray-200'>
        <div className='flex flex-wrap sm:flex-nowrap items-stretch h-full justify-between'>
          {/* Lifetime Access */}
          <div className='flex-1 bg-primary flex flex-col gap-y-2 items-center justify-center py-3 px-4 text-white font-semibold text-sm md:text-base rounded-tl-[12px] '>
            <Crown />
            <span className='text-center text-sm '>Lifetime Access</span>
          </div>

          {/* Certificate */}
          <div className='flex-1 bg-[#282829] flex flex-col gap-y-2 items-center justify-center py-3 px-4 text-white font-semibold text-sm md:text-base rounded-tr-[12px] '>
            <DiplomaIcon />
            <span className='text-center text-sm '>Earn a Certificate</span>
          </div>
        </div>
        <div className='grid grid-cols-3  items-stretch h-full justify-between'>
          {/* Course Level */}
          <div className='text-center  flex border-r-[2px]   flex-col items-center justify-center md:px-4 py-3 gap-y-1.5'>
            <p className='text-black font-bold text-sm  md:text-base capitalize'>
              {courseLevel}
            </p>
            <p className='font-medium max-w-[90px] text-[#504F53] text-xs'>
              Recommended experience
            </p>
          </div>

          {/* Divider */}
          {/* <div className='hidden md:w-[1px] bg-[#282829] min-h-[70px] border-r'></div> */}

          {/* Rating Section */}
          <ShouldRender check={rating}>
            <div className='text-center flex-1 border-r-[2px] border-l-[#504F53] flex flex-col justify-center py-2.5 md:px-5 gap-y-1'>
              <p className='text-black text-xl xl:text-2xl font-bold'>
                {rating}
              </p>
              <div className='flex justify-center items-center whitespace-nowrap'>
                <StarRating rating={rating} color='#CA8A04' />
              </div>
              <p
                onClick={() => handleScrollToBottom()}
                className='text-gray-500 cursor-pointer text-sm underline font-medium whitespace-nowrap'
              >
                {totalRatings} ratings
              </p>
            </div>
          </ShouldRender>

          {/* Divider */}
          {/* <div className='hidden md:w-[1px] bg-[#282829] min-h-[70px] border-r'></div> */}

          {/* Course Duration */}
          <div className='text-center  md:px-5 py-3 flex flex-col gap-y-1 justify-center items-center'>
            <ClockCircleFilled className='text-black text-2xl mb-1' />
            <p className='text-black text-base font-bold'>{duration}</p>
            <p className='text-[#504F53] text-sm font-medium'>Length</p>
          </div>
        </div>
      </div>
    </div>
  );

  const smInLine = (
    <div className='flex flex-col gap-y-4'>
      {/* Main Course Details */}
      <div className='flex flex-col gap-4 sm:flex-nowrap'>
        <span className='flex gap-x-2 items-center text-sm font-medium'>
          <CalendarIcon className='text-[#9F9EA6] w-5 h-5' />
          Uploaded on {moment(uploadedDate).format('MMM DD, YYYY')}
        </span>
        <span className='flex gap-x-2 items-center text-sm font-medium'>
          <GlobeIcon className='text-[#9F9EA6] w-5 h-5' />
          {language}
        </span>
        <span className='flex gap-x-2 items-center text-sm'>
          <SubTitle />
          Subtitle Available
        </span>
      </div>

      <div className='w-full bg-white shadow-lg flex flex-col rounded-[8px] border border-gray-200'>
        {/* Lifetime Access */}
        <div className='flex-1 bg-primary flex gap-2 items-center justify-start py-3 px-4 text-white min-h-[64px]  max-h-[64px]  font-semibold text-sm md:text-base rounded-t-[8px]'>
          <Crown width={screens.md ? '32' : '24'} />
          <span className='text-center text-sm'>Lifetime Access</span>
        </div>

        {/* Course Level */}
        <div className='flex-1 flex flex-col items-start justify-center border-r border-b px-5 py-3 gap-y-0.5 min-h-[64px] max-h-[64px] '>
          <p className='text-black font-bold text-sm md:text-base text-start capitalize'>
            {courseLevel}
          </p>
          <p className='font-medium text-start text-[#504F53] text-xs'>
            Recommended experience
          </p>
        </div>

        {/* Rating Section */}
        <ShouldRender check={rating}>
          <div className='flex-1 flex justify-between items-center border-r border-b px-5 py-3 gap-y-0.5 min-h-[64px] max-h-[64px] '>
            <div className='flex gap-x-2 items-center'>
              <p className='text-black text-xl xl:text-2xl font-bold'>
                {rating}
              </p>
              <div className='flex justify-center items-center whitespace-nowrap'>
                <StarRating rating={rating} color='#CA8A04' />
              </div>
            </div>
            <p
              onClick={() => handleScrollToBottom()}
              className='text-gray-500 text-xs  cursor-pointer underline font-medium whitespace-nowrap'
            >
              {totalRatings} ratings
            </p>
          </div>
        </ShouldRender>

        {/* Course Duration */}
        <div className='flex-1 flex justify-between items-center px-5 py-3 min-h-[64px] max-h-[64px] '>
          <div className='flex gap-x-2 items-center'>
            <ClockCircleFilled className='text-black text-lg' />
            <p className='text-black text-base font-bold'>{duration}</p>
          </div>
          <p className='text-[#504F53] text-xs font-medium'>Length</p>
        </div>

        {/* Certificate */}
        <div className='flex-1 bg-[#282829] flex gap-2 items-center justify-start py-3 px-5 text-white font-semibold text-sm min-h-[64px] max-h-[64px]  md:text-base rounded-b-[8px]'>
          <DiplomaIcon
            width={screens.md ? '28' : '24'}
            height={screens.md ? '24' : '20'}
          />
          <span className='text-center text-sm'>Earn a Certificate</span>
        </div>
      </div>
    </div>
  );
  return (
    <>
      <ShouldRender check={screens.xl}>{inlineComp}</ShouldRender>

      <ShouldRender check={screens.lg && !screens.xl}>{lgComp}</ShouldRender>
      <ShouldRender check={screens.md && !screens.lg}>
        {inlineComp}
      </ShouldRender>
      <ShouldRender check={!screens.md && !isSmallScreen}>
        {lgComp}
      </ShouldRender>

      <ShouldRender check={!screens.md && isSmallScreen}>
        {smInLine}
      </ShouldRender>
    </>
  );
};

export default CourseInfo;
